import React, { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Plus, Building2 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import { createClientColumns, Client } from "../lib/clientTableColumns";
import { SimpleDataTable } from "../components/simpleDataTable";

export default function Clients() {
  const navigate = useNavigate();
  const location = useLocation();
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>("");

  // Show success message if redirected from add client page
  const [successMessage, setSuccessMessage] = useState<string>("");

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the message from location state
      window.history.replaceState(null, "");

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage("");
      }, 5000);
    }
  }, [location.state]);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setIsLoading(true);
      setError("");

      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/clients`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Map backend data to frontend Client type
      const mappedClients: Client[] = response.data.data.map((client: any) => ({
        id: client._id,
        clientName: client.clientName,
        contactPerson: {
          name: client.contactPerson.name,
          email: client.contactPerson.email,
          phone: client.contactPerson.phone,
        },
        company: client.company,
        status: client.status,
        gstNumber: client.gstNumber,
        phoneNumber: client.phoneNumber,
        dateOfOnboarding: client.dateOfOnboarding,
        address: {
          city: client.address.city,
          state: client.address.state,
        },
        totalProjects: client.totalProjects || 0,
        activeProjects: client.activeProjects || 0,
      }));

      setClients(mappedClients);
    } catch (error: any) {
      console.error("Error fetching clients:", error);
      setError(error.response?.data?.message || "Failed to fetch clients");
    } finally {
      setIsLoading(false);
    }
  };

  // Callback function to refresh client list after deletion
  const handleClientDeleted = () => {
    fetchClients();
    // Show success message
    setSuccessMessage("Client deleted successfully!");
    // Auto-hide success message after 5 seconds
    setTimeout(() => {
      setSuccessMessage("");
    }, 5000);
  };

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center gap-4">
            <Spinner variant="ellipsis" size={40} className="text-primary" />
            <div className="text-center">
              <h3 className="text-lg font-medium">Loading Clients</h3>
              <p className="text-sm text-muted-foreground">
                Fetching client data from server...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="rounded-lg border bg-card p-8 text-center">
            <Building2 className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Error Loading Clients
            </h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={fetchClients}>Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Building2 className="h-8 w-8" />
            Clients
          </h1>
          <p className="text-muted-foreground">
            Manage your client information and onboarding details
          </p>
        </div>
        <Button onClick={() => navigate("/clients/add")} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Client
        </Button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded-full bg-green-500" />
            <p className="text-sm font-medium text-green-800">Success</p>
          </div>
          <p className="mt-1 text-sm text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Clients Table */}
      <div className="rounded-lg border bg-card">
        <SimpleDataTable
          columns={createClientColumns(handleClientDeleted)}
          data={clients}
          onDataChange={fetchClients}
        />
      </div>
    </div>
  );
}
